import { FormikErrors, useFormik } from 'formik';
import React from 'react';
import type { Contest } from '../../../stores/contest';
import {
    EMPTY_POLICIES_VALUES,
    PoliciesFormValues,
    policiesAfterSave,
    toPoliciesFormValues,
} from './form';

/**
 * The Policies tab's form. It starts over from each contest that loads, and
 * from a contest loaded again unless it holds changes not saved yet. Once a
 * save succeeds, `afterSave` starts it over from the reloaded contest, unless
 * the form changed while the save ran.
 */
export function usePoliciesForm(
    contest: Contest | null,
    validate: (values: PoliciesFormValues) => FormikErrors<PoliciesFormValues>
) {
    const formik = useFormik<PoliciesFormValues>({
        initialValues: EMPTY_POLICIES_VALUES,
        validate,
        onSubmit: () => undefined,
    });
    const { resetForm, values, dirty } = formik;

    // Read when the contest loads or a save resolves, after the render.
    const latestValues = React.useRef(values);
    const isDirty = React.useRef(dirty);
    React.useEffect(() => {
        latestValues.current = values;
        isDirty.current = dirty;
    });

    // A contest just created is the one its new policies were saved to.
    const loadedId = React.useRef(contest?.id ?? null);
    React.useEffect(() => {
        const id = contest?.id ?? null;
        if (id !== loadedId.current || !isDirty.current) {
            loadedId.current = id;
            resetForm({
                values: contest
                    ? toPoliciesFormValues(contest)
                    : EMPTY_POLICIES_VALUES,
            });
        }
    }, [contest, resetForm]);

    const afterSave = React.useCallback(
        (submitted: PoliciesFormValues, reloaded: Contest | null) => {
            const next = policiesAfterSave(
                submitted,
                latestValues.current,
                reloaded
            );
            if (next) {
                resetForm({ values: next });
            }
        },
        [resetForm]
    );

    return { formik, afterSave };
}
